// PROOF — Journal posts mock data.
// Shape mirrors a Sanity `journalPost` — replaceable 1:1 with GROQ fetch later.

const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString();
};

export const JOURNAL = [
    {
        slug: "the-weight-of-cotton",
        title: "The Weight of Cotton",
        excerpt:
            "Why we cut Field Tee 01 at 320 gsm — and why every gram below it lies.",
        cover: "/assets/yk/products/p1.png",
        bgTone: "#e8e7e1",
        publishedAt: daysAgo(6),
        readMinutes: 4,
        author: "PROOF Atelier",
        tags: ["materials", "field-001"],
        body: [
            { type: "p", text: "A tee shirt is a contract. It promises to outlast the season it was bought in." },
            { type: "p", text: "Most fail. Light cotton frays at the neck, sags at the hem, sheds its first true black after eleven washes. We do not make tees for the first wash. We make them for the eleventh." },
            { type: "h2", text: "320 grams per square metre" },
            { type: "p", text: "Field Tee 01 is cut from 320 gsm cotton — a weight that sits between the conventional 180 gsm 'premium' tee and the 400 gsm Japanese loopback. Heavy enough to keep its shoulder line for a decade; light enough to wear in July." },
            { type: "p", text: "Below 280 gsm the fabric lies. Above 360 it becomes a sweatshirt. The window is narrow." },
            { type: "pull", text: "We do not make tees for the first wash. We make them for the eleventh." },
            { type: "h2", text: "Garment-dyed, never piece-dyed" },
            { type: "p", text: "Each tee is sewn in raw cotton, then dipped in dye after construction. The result is a colour that lives inside the seams rather than on top of them. When you wash it, the dye fades evenly — across the body, the cuff, the print of your own shoulder. That is what aging looks like when nothing about it shouts." },
        ],
    },
    {
        slug: "atelier-notes-warsaw",
        title: "Atelier Notes · Warsaw",
        excerpt:
            "Three rooms, four pattern-makers, one rule: the garment leaves only when nothing else can be removed.",
        cover: "/assets/yk/products/p4.png",
        bgTone: "#d2d1cb",
        publishedAt: daysAgo(14),
        readMinutes: 6,
        author: "Studio Diary",
        tags: ["studio", "process"],
        body: [
            { type: "p", text: "Walk up two flights of stairs in a quiet street off Próżna. Past the courtyard, through the third door on the left. That is where PROOF is cut." },
            { type: "h2", text: "Room one — patterns" },
            { type: "p", text: "Long table. Two large windows facing east. A single Singer 281, a roll of toile, and a wall of paper patterns indexed by drop number. This is where the season slows down." },
            { type: "h2", text: "Room two — samples" },
            { type: "p", text: "Every cut is sewn twice in toile, once in fashion fabric, and only then approved. We refuse to release a piece until we ourselves have worn it for two weeks straight." },
            { type: "pull", text: "The garment leaves only when nothing else can be removed." },
            { type: "h2", text: "Room three — packing" },
            { type: "p", text: "Pieces fold here. By hand, on the same table where the first toile was cut. Tape, no plastic. Tissue, no inserts. A single mono-printed thank-you card per parcel." },
        ],
    },
    {
        slug: "field-002-the-second-voice",
        title: "Field 002 · The Second Voice",
        excerpt:
            "The atelier slows for winter. What boiled wool taught us about restraint.",
        cover: "/assets/yk/products/p2.png",
        bgTone: "#e8e7e1",
        publishedAt: daysAgo(22),
        readMinutes: 5,
        author: "PROOF Atelier",
        tags: ["field-002", "wool"],
        body: [
            { type: "p", text: "Field 002 was cut over the coldest weeks of January. The pieces are heavier, fewer, more closely related — a single fabric thesis: how does a garment age when nothing about it shouts?" },
            { type: "h2", text: "On boiled wool" },
            { type: "p", text: "Boiling wool compresses the fibres until the fabric reaches the density of felted leather. Cut and sewn, the seams disappear. The garment becomes a single field of structure." },
            { type: "pull", text: "The seams disappear. The garment becomes a single field of structure." },
            { type: "h2", text: "On the second voice" },
            { type: "p", text: "Every brand begins by speaking loudly. The second collection is the one in which it learns to whisper. Field 002 is that collection for us." },
        ],
    },
    {
        slug: "less-noise-a-manifesto",
        title: "Less Noise · A Manifesto",
        excerpt:
            "Why we refuse to chase trends — and why your wardrobe will thank you for it.",
        cover: "/assets/yk/products/p8.png",
        bgTone: "#d2d1cb",
        publishedAt: daysAgo(40),
        readMinutes: 3,
        author: "Editorial",
        tags: ["manifesto"],
        body: [
            { type: "p", text: "Hype is a tax on uncertainty. The more uncertain a brand is about what it actually stands for, the more it shouts." },
            { type: "p", text: "We are not interested in shouting. PROOF was built around four ideas: heavy construction, honest materials, slow manufacture, and quiet identity. Everything else is noise." },
            { type: "pull", text: "Hype is a tax on uncertainty." },
            { type: "p", text: "If the product is built right, it does not need a story to sell it. The story is in the seams." },
        ],
    },
    {
        slug: "an-honest-supply-chain",
        title: "An Honest Supply Chain",
        excerpt:
            "Where each piece comes from, who touched it, and why we still answer our own phone.",
        cover: "/assets/yk/products/p5.png",
        bgTone: "#e8e7e1",
        publishedAt: daysAgo(58),
        readMinutes: 7,
        author: "Studio Diary",
        tags: ["studio", "transparency"],
        body: [
            { type: "p", text: "Every garment we make passes through fewer than ten pairs of hands between the mill and your wardrobe. We can name all of them." },
            { type: "h2", text: "Cotton — Pomerania" },
            { type: "p", text: "Sourced from a Pomeranian mill that has been spinning long-staple cotton since 1948. They send us 320 gsm jersey on five-metre rolls, twice a year." },
            { type: "h2", text: "Wool — Lower Silesia" },
            { type: "p", text: "Boiled wool comes from a family-run mill in Lower Silesia. They run two looms. We have one of two yearly slots." },
            { type: "h2", text: "Sewing — Warsaw" },
            { type: "p", text: "Cut, sewn and finished in our own atelier on Próżna street. Four pattern-makers. Two sewists. One pair of shears that has been with the studio since the first sample." },
            { type: "pull", text: "We answer our own phone. We pack our own boxes." },
            { type: "h2", text: "Shipping — InPost & DHL" },
            { type: "p", text: "Polish orders ship via InPost paczkomat — the most efficient last-mile network in the EU. International via DHL Express. Returns through Wygodne Zwroty, a Polish-grown service that handles refunds in five days flat." },
        ],
    },
];

export const getJournalBySlug = (slug) => JOURNAL.find((j) => j.slug === slug) || null;
